import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@heroui/react";
import { ERROR_CODE, TemplatePtAPI } from "@repo/api";
import { storageKeys, templateKeys } from "@repo/query";

type ErrorLike = { status?: number; msg?: string } | null | undefined;

/**
 * 模板详情（web / webview 共用）。
 * 403 不弹错误而是交给页面渲染「无权限」态；其它错误由页面自行 toast。
 */
export function useTemplateDetail(
  templateId: string | undefined,
  sendId: string | undefined,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: templateKeys.detail(templateId ?? "", sendId),
    queryFn: () => TemplatePtAPI.getTemplateDetail(templateId!, sendId).then((r) => r.data),
    enabled: enabled && Boolean(templateId),
  });
  // 后端对「非签收方 / 违规禁访」返回业务码 403（不是 4003 无权限）
  const noPermission = (query.error as ErrorLike)?.status === 403;
  return { ...query, noPermission };
}

/** 某模板的发货记录表，翻页时保留上一页数据 */
export function useSendRecords(templateId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: templateKeys.sendRecords(templateId, { page, pageSize }),
    queryFn: () => TemplatePtAPI.getSendRecord(templateId, page, pageSize).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
}

/**
 * 详情页全部写操作。成功后统一失效整个模板域（详情 / 列表 / 角标 / 发货记录），
 * 涉及云空间的两个操作额外失效配额。
 * 错误 toast 在这里统一处理；`deleteTemplate` 遇到「仍有未签收发货」不 toast，由页面弹引导框。
 */
export function useTemplateDetailMutations() {
  const queryClient = useQueryClient();

  const invalidateTemplates = () => {
    void queryClient.invalidateQueries({ queryKey: templateKeys.all });
  };
  const invalidateTemplatesAndQuota = () => {
    invalidateTemplates();
    void queryClient.invalidateQueries({ queryKey: storageKeys.quota() });
  };
  const toastError = (fallback: string) => (error: ErrorLike) => {
    toast.danger(error?.msg || fallback);
  };

  const receive = useMutation({
    mutationFn: (sendId: string) => TemplatePtAPI.receiveTemplate(sendId),
    onSuccess: () => {
      toast.success("签收成功");
      invalidateTemplates();
    },
    onError: toastError("签收失败，请重试"),
  });

  const retryMask = useMutation({
    // 延迟一秒再执行
    mutationFn: (sendId: string) =>
      new Promise<void>((resolve) => setTimeout(resolve)).then(() =>
        TemplatePtAPI.retryMask(sendId),
      ),
    onSuccess: () => {
      toast.success("重试已经发起，请稍后刷新页面查看");
      invalidateTemplates();
    },
    onError: toastError("重试失败，请稍后再试"),
  });

  const deleteTemplate = useMutation({
    mutationFn: (templateId: string) => TemplatePtAPI.deleteTemplate(templateId),
    onSuccess: () => {
      toast.success("删除成功");
      invalidateTemplatesAndQuota();
    },
    onError: (error: ErrorLike) => {
      if (error?.status === ERROR_CODE.HAS_UNRECEIVED_SENDS) return;
      toast.danger(error?.msg || "删除失败，请重试");
    },
  });

  const storageReceive = useMutation({
    mutationFn: (sendId: string) => TemplatePtAPI.storageReceiveTemplate(sendId),
    onSuccess: () => {
      toast.success("已转为永久存储");
      invalidateTemplatesAndQuota();
    },
    onError: toastError("转移失败，请重试"),
  });

  const deleteStorageReceive = useMutation({
    mutationFn: (sendId: string) => TemplatePtAPI.deleteStorageReceiveTemplate(sendId),
    onSuccess: () => {
      toast.success("已从云空间删除");
      invalidateTemplatesAndQuota();
    },
    onError: toastError("删除失败，请重试"),
  });

  const retract = useMutation({
    mutationFn: (recordId: number) => TemplatePtAPI.retract(recordId),
    onSuccess: () => {
      toast.success("撤回成功");
      invalidateTemplates();
    },
    onError: toastError("撤回失败，请重试"),
  });

  return { receive, retryMask, deleteTemplate, storageReceive, deleteStorageReceive, retract };
}
